import React, { useState, useEffect } from 'react'
import { useQuery } from '@apollo/client'
import { Modal, Row, Col, Spin, List, Button, Typography, Popover } from 'antd'
import { RetweetOutlined } from '@ant-design/icons'
import { Panel } from '../../../../core/components/styled'
import { formatDate } from '../../../../core/utils/services/formatter'
import { SCRAPING_LOGS_FOR_SESSION } from '../../../graphql/queries'
import ScrapingLogSeverity from './scraping-log-severity'
import ScrapingLogSeverityFilter from './scraping-log-severity-filter'
import ScrapingSessionMenuFilter from './scraping-session-menu-filter'
import PropTypes from 'prop-types'

const ScrapingSessionLogsModal = ({
  title,
  sessionId,
  menuId,
  isModalOpen,
  closeModal,
}) => {
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState({})

  useEffect(() => {
    setFilters({ menuId })
  }, [menuId])

  const {
    data: { scrapingLogsForSessionId = {} } = {},
    loading: isLoadingLogs,
    refetch: refetchLogs,
  } = useQuery(SCRAPING_LOGS_FOR_SESSION, {
    variables: { sessionId, page, filters },
    skip: !sessionId || (!filters.menuId && !filters.severity),
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  })

  return (
    isModalOpen && (
      <Modal
        width={1000}
        bodyStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
        title={
          <Row>
            <Typography.Text>{title}</Typography.Text>
            <Popover trigger="hover" placement="right" content="Refetch logs">
              <Button
                size="small"
                style={{ marginLeft: '25px' }}
                icon={<RetweetOutlined />}
                onClick={() => refetchLogs()}
              />
            </Popover>
          </Row>
        }
        visible
        footer={null}
        onCancel={closeModal}
      >
        <Panel>
          <Row gutter={16}>
            <Col span={6}>
              <ScrapingLogSeverityFilter
                severity={filters.severity}
                onChange={severity => {
                  setFilters(prevFilters => ({ ...prevFilters, severity }))
                  setPage(1)
                }}
              />
            </Col>
            {!menuId && (
              <Col span={18}>
                <ScrapingSessionMenuFilter
                  sessionId={sessionId}
                  menuId={filters.menuId}
                  onChange={menuId => {
                    setFilters(prevFilters => ({ ...prevFilters, menuId }))
                    setPage(1)
                  }}
                />
              </Col>
            )}
          </Row>
        </Panel>
        {isLoadingLogs ? (
          <Row type="flex" justify="center">
            <Spin />
          </Row>
        ) : (
          <List
            bordered
            dataSource={scrapingLogsForSessionId.scrapingLogs}
            renderItem={scrapingLog => (
              <List.Item>
                <ScrapingLogSeverity severity={scrapingLog.severity} />
                <Typography.Text
                  style={{
                    color: '#556b87',
                    marginRight: '5px',
                  }}
                >
                  {`${formatDate(scrapingLog.timestamp)}`}
                </Typography.Text>
                {`${scrapingLog.message}`}
              </List.Item>
            )}
            pagination={{
              current: page,
              pageSize: 100,
              showSizeChanger: false,
              total: scrapingLogsForSessionId.total,
              onChange: page => setPage(page),
            }}
          />
        )}
      </Modal>
    )
  )
}

ScrapingSessionLogsModal.propTypes = {
  title: PropTypes.string,
  sessionId: PropTypes.string,
  menuId: PropTypes.string,
  isModalOpen: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
}

ScrapingSessionLogsModal.defaultProps = {
  title: 'Scraping logs',
  sessionId: undefined,
  menuId: undefined,
}

export default ScrapingSessionLogsModal
